// src/App.tsx
import Layout from './components/Layout/Layout';
import { KanbanAPI, NotificationItem,primeCsrf } from './pages/kanban';
import RepresentativeEmployeesPage from './pages/RepresentativeEmployeesPage';
import RepresentativeCasePage from './pages/RepresentativeCasePage';


import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { BackgroundProvider } from './contexts/BackgroundContext';
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { createAdaptivePoller } from './lib/polling';
import { apiJson } from './lib/apiClient';
import { csrfFetch } from './utils/csrfFetch';
import { BACKEND } from './config';
import { ErrorBoundary } from './components/ErrorBoundary';
import LoadingSpinner from './components/Spinner/LoadingSpinner';

const SheetsPage = lazy(() => import('./pages/SheetsPage'));
const ChairmanHomePage = lazy(() => import('./pages/ChairmanHomePage'));
const ChsiPage = lazy(() => import('./pages/ChsiPage'));
const ChsiSummaryPage = lazy(() => import('./pages/ChsiSummaryPage'));
const StatementsPage = lazy(() => import('./pages/StatementsPage'));
const AgentsDataPage = lazy(() => import('./pages/AgentsDataPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const RepresentativeTasksPage = lazy(() => import('./pages/RepresentativeTasksPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const DossierPage = lazy(() => import('./pages/DossierPage'));
const SummaryPage = lazy(() => import('./pages/SummaryPage'));
const TimesheetPage = lazy(() => import('./pages/TimesheetPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const ActivityPage = lazy(() => import('./pages/ActivityPage'));
const LitigationSummaryPage = lazy(() => import('./pages/LitigationSummaryPage'));
const LegalTemplatesPage = lazy(() => import('./pages/LegalTemplatesPage'));
const LitigationStatusPage = lazy(() => import('./pages/LitigationStatusPage'));
const MediationPage = lazy(() => import('./pages/MediationPage'));
const LitigationPage = lazy(() => import('./pages/LitigationPage'));



type LocalDeadlineReminder = {
  id: number;
  userId: number;
  taskId: number;
  taskTitle: string;
  fireAt: string;
  minutes: number;
};

const DEADLINE_REMINDER_KEY = (scope: string) => `kanban:deadline-reminders:${scope}`;

type DataSyncState = {
  scope: string;
  changed: boolean;
  latest_event_at: string | null;
  version?: string;
  entity?: string;
  excel_file_id?: number;
  row_id?: number;
  cell_id?: string;
  field?: string;
  value?: unknown;
  value_type?: 'number' | 'string' | 'boolean' | 'null';
  action?: 'upsert' | 'deleted' | 'patched' | 'cell_patched' | 'cell_deleted';
  event_id?: string;
  source_action?: string;
  occurred_at?: string;
};

const NotificationModal: React.FC<{
  n: NotificationItem;
  onOk: () => void;
  onNotOk: () => void;
  onClose: () => void;
}> = ({ n, onOk, onNotOk, onClose }) => {
  const node = useMemo(() => document.body, []);

  const modal = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999, // выше всего
        pointerEvents: 'none',
      }}
    >
      {/* overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.42)',
          pointerEvents: 'none',
        }}
      />

      {/* box: TOP-CENTER */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'absolute',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(560px, calc(100vw - 24px))',
          background: '#fff',
          borderRadius: 14,
          border: '1px solid #d8dee7',
          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
          padding: 16,
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#0f172a' }}>{n.title}</div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.45, color: '#334155' }}>{n.message}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: '1px solid #d8dee7',
              borderRadius: 10,
              background: '#ffffff',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              width: 34,
              height: 34,
            }}
            aria-label="Close"
            title="Закрыть"
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
          <button
            onClick={onNotOk}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderRadius: 10,
              border: '1px solid #d8dee7',
              background: '#fff',
              color: '#334155',
              fontWeight: 600,
            }}
          >
            ✕
          </button>

          <button
            onClick={onOk}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderRadius: 10,
              border: '1px solid #1f4fa3',
              background: '#1f4fa3',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            ОК
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, node);
};

const NotificationsListener: React.FC = () => {
  const { isAuthenticated, userId } = useAuth();
  const timerRef = useRef<number | null>(null);
  const dismissedRef = useRef<Set<number>>(new Set());

  const storageKey = useMemo(() => `kanban:dismissed:${userId ?? 'guest'}`, [userId]);
  const reminderStorageKey = useMemo(() => DEADLINE_REMINDER_KEY(String(userId ?? 'guest')), [userId]);

  const pullDueReminderNotifications = useCallback((): NotificationItem[] => {
    try {
      const raw = localStorage.getItem(reminderStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      const nowTs = Date.now();
      const due: NotificationItem[] = [];
      const keep: LocalDeadlineReminder[] = [];

      parsed.forEach((item) => {
        const reminder = item as LocalDeadlineReminder;
        if (!reminder || reminder.userId !== userId) return;
        const fireTs = new Date(reminder.fireAt).getTime();
        if (!Number.isFinite(fireTs)) return;

        if (fireTs <= nowTs) {
          due.push({
            id: reminder.id,
            type: 'deadline_reminder',
            title: 'Напоминание по задаче',
            message: `Пора заняться задачей: ${reminder.taskTitle}`,
            payload: { task_id: reminder.taskId, minutes: reminder.minutes },
            created_at: new Date().toISOString(),
            is_read: false,
          });
        } else {
          keep.push(reminder);
        }
      });

      localStorage.setItem(reminderStorageKey, JSON.stringify(keep));
      return due;
    } catch {
      return [];
    }
  }, [reminderStorageKey, userId]);

  const loadDismissed = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return new Set<number>();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set<number>();
      return new Set<number>(parsed.filter((id) => Number.isInteger(id)));
    } catch {
      return new Set<number>();
    }
  }, [storageKey]);

  const saveDismissed = React.useCallback((next: Set<number>) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(next)));
    } catch {
      // ignore storage errors
    }
  }, [storageKey]);

  const [queue, setQueue] = useState<NotificationItem[]>([]);
  const current = queue[0] ?? null;

  const ack = async (id: number, response: 'ok' | 'not_ok') => {
    if (id < 0) return;
    try {
      await KanbanAPI.ackNotification(id, response);
    } catch (e) {
      console.error('Не удалось подтвердить уведомление', e);
    }
  };


  const pop = () => setQueue((q) => q.slice(1));

  const respondAndPop = (id: number, response: 'ok' | 'not_ok') => {
    dismissedRef.current.add(id);
    saveDismissed(dismissedRef.current);
    pop();
    void ack(id, response);
  };


  const poll = useCallback(async () => {
    if (!isAuthenticated) return;
    const incoming = await KanbanAPI.listNotifications(1);
    const localReminders = pullDueReminderNotifications();
    const mergedIncoming = [...incoming, ...localReminders];

    setQueue((prev) => {
      const existingIds = new Set(prev.map(n => n.id));
      const next = [...prev];
      for (const n of mergedIncoming) {
        if (!existingIds.has(n.id) && !dismissedRef.current.has(n.id)) next.push(n);
      }
      return next;
    });
  }, [isAuthenticated, pullDueReminderNotifications]);


  useEffect(() => {
    dismissedRef.current = loadDismissed();
  }, [loadDismissed]);

  useEffect(() => {
    if (!isAuthenticated) {
      setQueue([]);
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const stopPolling = createAdaptivePoller(() => poll(), { intervalMs: 15000, hiddenIntervalMs: 30000, maxIntervalMs: 60000 });
    return () => {
      stopPolling();
      timerRef.current = null;
    };
  }, [isAuthenticated, userId, poll]);

  if (!current) return null;

  return (
    <NotificationModal
      n={current}
      onOk={() => {
        respondAndPop(current.id, 'ok');
      }}
      onNotOk={() => {
        respondAndPop(current.id, 'not_ok');
      }}
      onClose={() => {
        respondAndPop(current.id, 'not_ok');
      }}
    />
  );
};


const pathToScope = (pathname: string, role?: string | null): string => {
  if (pathname.startsWith('/tasks') || pathname.startsWith('/representative/tasks')) return 'kanban';
  if (pathname.startsWith('/documents') || pathname.startsWith('/statements-list')) return 'documents';
  if (pathname.startsWith('/chsi')) return 'chsi';
  if (pathname.startsWith('/summary')) return 'summary';
  if (pathname.startsWith('/representative/case')) return 'representative';
  if (pathname.startsWith('/chairman')) return 'sheets';

  // Для админа/менеджера/председателя на основном табличном контуре
  // берём global, иначе можно пропустить изменения из смежных модулей
  // (например, загрузки документов, влияющие на агрегаты).
  if (pathname.startsWith('/sheets') || pathname.startsWith('/dossier')) {
    if (role === 'admin' || role === 'manager' || role === 'chairman') return 'global';
    return 'sheets';
  }

  return 'global';
};

const shouldPreferSoftRefresh = (pathname: string): boolean => {
  return pathname.startsWith('/sheets')
    || pathname.startsWith('/documents')
    || pathname.startsWith('/summary')
    || pathname.startsWith('/chairman')
    || pathname.startsWith('/chsi');
};

const GlobalDataSyncListener: React.FC<{ onChanged: (options?: { hard?: boolean; scope?: string; event?: DataSyncState }) => void }> = ({ onChanged }) => {
  const { isAuthenticated, userId, role } = useAuth();
  const { pathname } = useLocation();
  const timerRef = useRef<number | null>(null);
  const lastSoftRefreshAtRef = useRef<number>(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const sourceRef = useRef<EventSource | null>(null);
  const dispatchTimerRef = useRef<number | null>(null);
  const pendingEventRef = useRef<DataSyncState | null>(null);
  const reconnectAttemptRef = useRef<number>(0);
  const sseErrorCountRef = useRef<number>(0);
  const focusRetryHandlerRef = useRef<(() => void) | null>(null);
  const [sseFailed, setSseFailed] = useState(false);

  const scope = useMemo(() => pathToScope(pathname, role), [pathname, role]);
  const storageKey = useMemo(() => `crm:data-sync:last:${userId ?? 'guest'}:${scope}`, [userId, scope]);
  const softSyncDispatchDelayMs = 300;

  const dispatchSyncChanged = useCallback((event: DataSyncState) => {
    lastSoftRefreshAtRef.current = Date.now();
    const prefersSoftRefresh = shouldPreferSoftRefresh(pathname);
    onChanged({ hard: !prefersSoftRefresh, scope, event });
  }, [onChanged, pathname, scope]);

  const scheduleSyncChanged = useCallback((event: DataSyncState) => {
    pendingEventRef.current = event;
    if (dispatchTimerRef.current != null) return;

    dispatchTimerRef.current = window.setTimeout(() => {
      dispatchTimerRef.current = null;
      const pending = pendingEventRef.current;
      pendingEventRef.current = null;
      if (pending) {
        dispatchSyncChanged(pending);
      }
    }, softSyncDispatchDelayMs);
  }, [dispatchSyncChanged]);

  const pull = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated || document.hidden) return false;

    const sinceVersion = sessionStorage.getItem(storageKey) || '';
    const query = sinceVersion
      ? `?scope=${encodeURIComponent(scope)}&since_version=${encodeURIComponent(sinceVersion)}`
      : `?scope=${encodeURIComponent(scope)}`;

    const data = await apiJson<DataSyncState>(`/api/data-sync/state/${query}`);
    if (data.version) {
      sessionStorage.setItem(storageKey, data.version);
    } else if (data.latest_event_at) {
      // fallback для обратной совместимости
      sessionStorage.setItem(storageKey, data.latest_event_at);
    }

    if (data.changed) {
      scheduleSyncChanged(data);
      return true;
    }

    return false;
  }, [isAuthenticated, scope, storageKey, scheduleSyncChanged]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (dispatchTimerRef.current != null) {
        window.clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }
      sourceRef.current?.close();
      sourceRef.current = null;
      pendingEventRef.current = null;
      reconnectAttemptRef.current = 0;
      sseErrorCountRef.current = 0;
      setSseFailed(false);
      return;
    }

    const reconnectDelaysMs = [1000, 2000, 5000, 10000, 30000];
    const maxSseErrorsBeforePolling = 5;

    const connect = () => {
      const sinceVersion = sessionStorage.getItem(storageKey) || '';
      const query = sinceVersion
        ? `?scope=${encodeURIComponent(scope)}&since_version=${encodeURIComponent(sinceVersion)}`
        : `?scope=${encodeURIComponent(scope)}`;

      const source = new EventSource(`${BACKEND}/api/data-sync/stream/${query}`, { withCredentials: true });
      sourceRef.current = source;

      const onChangedEvent = (event: Event) => {
        const msg = event as MessageEvent<string>;
        let parsed: DataSyncState = { scope, changed: true, latest_event_at: null };
        try {
          parsed = JSON.parse(msg.data || '{}') as DataSyncState;
          if (parsed.version) {
            sessionStorage.setItem(storageKey, parsed.version);
          } else if (parsed.latest_event_at) {
            sessionStorage.setItem(storageKey, parsed.latest_event_at);
          }
        } catch (error) {
          console.debug('[soft-sync] fallback_full_refresh', { reason: 'invalid_payload', scope, error });
        }

        scheduleSyncChanged(parsed);
      };

      source.addEventListener('data_sync_changed', onChangedEvent);
      source.onopen = () => {
        reconnectAttemptRef.current = 0;
        sseErrorCountRef.current = 0;
        setSseFailed(false);
      };
      source.onerror = () => {
        source.removeEventListener('data_sync_changed', onChangedEvent);
        source.close();

        sseErrorCountRef.current += 1;
        const reconnectDelay = reconnectDelaysMs[Math.min(reconnectAttemptRef.current, reconnectDelaysMs.length - 1)];
        reconnectAttemptRef.current += 1;

        if (sseErrorCountRef.current >= maxSseErrorsBeforePolling) {
          console.debug('[soft-sync] sse_error_fallback_polling', {
            scope,
            errors: sseErrorCountRef.current,
            reconnect_delay_ms: reconnectDelay,
          });
          setSseFailed(true);
          // СТОП бесконечному SSE-реконнекту (мёртвая сессия молотила 401 всю ночь —
          // night_watch 2026-07-03, sync из BAS). Дальше работает since-поллинг; SSE
          // вернём по фокусу окна, если check-session подтвердит живую сессию (401 при
          // мёртвой сам уронит isAuthenticated через session-expiry).
          if (!focusRetryHandlerRef.current) {
            const onFocus = () => {
              void csrfFetch(`${BACKEND}/api/check-session/`)
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => {
                  if (d?.authenticated) {
                    window.removeEventListener('focus', onFocus);
                    focusRetryHandlerRef.current = null;
                    sseErrorCountRef.current = 0;
                    reconnectAttemptRef.current = 0;
                    setSseFailed(false);
                    connect();
                  }
                })
                .catch(() => { /* сеть — попробуем на следующем фокусе */ });
            };
            focusRetryHandlerRef.current = onFocus;
            window.addEventListener('focus', onFocus);
          }
          return;
        }

        if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          connect();
        }, reconnectDelay);
      };
    };

    connect();

    return () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (dispatchTimerRef.current != null) {
        window.clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = null;
      }
      sourceRef.current?.close();
      sourceRef.current = null;
      pendingEventRef.current = null;
      if (focusRetryHandlerRef.current) {
        window.removeEventListener('focus', focusRetryHandlerRef.current);
        focusRetryHandlerRef.current = null;
      }
    };
  }, [isAuthenticated, scope, storageKey, scheduleSyncChanged]);


  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    if (!sseFailed) {
      return;
    }

    const stopPolling = createAdaptivePoller(async () => {
      const changed = await pull();
      if (changed) return;
    }, { intervalMs: 5000, hiddenIntervalMs: 15000, maxIntervalMs: 30000 });

    return () => {
      stopPolling();
      timerRef.current = null;
    };
  }, [isAuthenticated, sseFailed, pull, onChanged]);

  return null;
};

const DefaultPage: React.FC = () => {
  const { role, isLawyer, isMediator } = useAuth();
  const roleCode = String(role || '');
  if (roleCode === 'chairman') return <Navigate to="/chairman" replace />;
  if (roleCode === 'representative') return <Navigate to="/summary" replace />;
  if (isLawyer) return <Navigate to="/litigation" replace />;
  if (!isLawyer && isMediator) return <Navigate to="/mediation" replace />;
  if (roleCode === 'notary' || roleCode === 'secondary') return <Navigate to="/summary" replace />;
  if (roleCode === 'representative_employee') return <Navigate to="/chairman" replace />;
  return <SheetsPage />;
};



const SheetsRoute: React.FC = () => {
  const { role, isLawyer, isMediator } = useAuth();
  const roleCode = String(role || '');
  if (roleCode === 'secondary' || isLawyer || isMediator) return <Navigate to="/summary" replace />;
  return <SheetsPage />;
};

const SummaryRoute: React.FC = () => {
  const { isLawyer, isMediator } = useAuth();
  if (isLawyer) return <Navigate to="/litigation" replace />;
  if (!isLawyer && isMediator) return <Navigate to="/mediation" replace />;
  return <SummaryPage />;
};

const DossierRoute: React.FC = () => {
  const { isLawyer, isMediator } = useAuth();
  if (isLawyer) return <Navigate to="/litigation" replace />;
  if (!isLawyer && isMediator) return <Navigate to="/mediation" replace />;
  return <DossierPage />;
};

const TimesheetRoute: React.FC = () => {
  const { username } = useAuth();
  const allowedTimesheetUsers = new Set(['serik', 'suresan']);
  if (!username || !allowedTimesheetUsers.has(username.toLowerCase())) return <Navigate to="/" replace />;
  return <TimesheetPage />;
};

const DocumentsRoute: React.FC = () => {
  const { role, isLawyer, isMediator } = useAuth();
  const roleCode = String(role || '');
  if (roleCode === 'notary' || roleCode === 'secondary' || isLawyer || isMediator) return <Navigate to="/summary" replace />;
  return <DocumentsPage />;
};

const ChsiRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  if (roleCode === 'notary') return <Navigate to="/summary" replace />;
  return <ChsiPage />;
};

const HistoryRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  if (roleCode !== 'admin' && roleCode !== 'manager') return <Navigate to="/" replace />;
  return <HistoryPage />;
};

const ActivityRoute: React.FC = () => {
  const { role } = useAuth();
  const roleCode = String(role || '');
  if (roleCode !== 'admin' && roleCode !== 'manager') return <Navigate to="/" replace />;
  return <ActivityPage />;
};


const RouterComponent = (window as any).desktopApp ? HashRouter : BrowserRouter;

const App: React.FC = () => {
  const handleSyncChange = useCallback((options?: { hard?: boolean; scope?: string; event?: DataSyncState }) => {
    window.dispatchEvent(new CustomEvent('crm:data-sync-soft', {
      detail: {
        scope: options?.scope || options?.event?.scope || 'global',
        entity: options?.event?.entity || 'registry',
        excel_file_id: options?.event?.excel_file_id,
        action: options?.event?.action,
        row_id: options?.event?.row_id,
        cell_id: options?.event?.cell_id,
        field: options?.event?.field,
        value: options?.event?.value,
        value_type: options?.event?.value_type,
        version: options?.event?.version,
        event_id: options?.event?.event_id,
        source_action: options?.event?.source_action,
        occurred_at: options?.event?.occurred_at || options?.event?.latest_event_at,
        changed: options?.event?.changed,
        latest_event_at: options?.event?.latest_event_at,
        hard_requested: Boolean(options?.hard),
      },
    }));
  }, []);

  useEffect(() => {
    primeCsrf().catch(() => {});
  }, []);


  return (
    <BackgroundProvider>
      <RouterComponent>
        <NotificationsListener />
        <GlobalDataSyncListener onChanged={handleSyncChange} />

        <Suspense fallback={<LoadingSpinner />}><ErrorBoundary><Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route path="/" element={<Layout />}>
            <Route index element={<DefaultPage />} />
            <Route path="sheets" element={<SheetsRoute />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="representative/tasks" element={<RepresentativeTasksPage />} />
            <Route path="documents" element={<DocumentsRoute />} />
            <Route path="dossier" element={<DossierRoute />} />
            <Route path="chairman" element={<ChairmanHomePage />} />
            <Route path="chsi" element={<ChsiRoute />} />
            <Route path="chsi-summary" element={<ChsiSummaryPage />} />
            <Route path="statements-list" element={<StatementsPage />} />
            <Route path="agents-data" element={<AgentsDataPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="history" element={<ActivityRoute />} />
            <Route path="history-changes" element={<HistoryRoute />} />
            <Route path="summary" element={<SummaryRoute />} />
            <Route path="timesheet" element={<TimesheetRoute />} />
            <Route path="chairman/employees" element={<RepresentativeEmployeesPage />} />
            <Route path="chairman/case" element={<RepresentativeCasePage />} />
            <Route path="representative/employees" element={<RepresentativeEmployeesPage />} />
            <Route path="representative/case" element={<RepresentativeCasePage />} />
            <Route path="litigation" element={<LitigationPage />} />
            <Route path="mediation" element={<MediationPage />} />
            <Route path="litigation-status" element={<LitigationStatusPage />} />
            <Route path="templates" element={<LegalTemplatesPage />} />
            <Route path="litigation-summary" element={<LitigationSummaryPage />} />

          </Route>
        </Routes></ErrorBoundary></Suspense>
      </RouterComponent>
    </BackgroundProvider>
  );
};

export default App;
